import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  ErrorState,
  LoadingSkeleton,
  SectionCard,
  Segmented,
  TextInput,
  ToggleInput,
} from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { AppConfigRow } from "../components/AppConfigRow";
import { AddAppConfigDialog } from "../components/AddAppConfigDialog";
import { groupConfigs, type SectionId } from "../lib/configSections";
import { useAppConfigsQuery } from "../services/useAppConfigsQuery";

type Props = {
  locale: string;
};

// Scope filter. "custom" isn't an option here — the Custom section has its own
// tab, so offering it in both places would be two controls for one intent.
type Segment = "all" | "runtime" | "boot";

const SECTION_TITLE_KEY: Record<SectionId, string> = {
  shipping: keys.appConfigs.catShipping,
  payments: keys.appConfigs.catPayments,
  messaging: keys.appConfigs.catMessaging,
  orders: keys.appConfigs.catOrder,
  ai: keys.appConfigs.catAi,
  system: keys.appConfigs.catSystem,
  custom: keys.appConfigs.sectionCustom,
};

export function AppConfigsPage({ locale }: Props) {
  const navigate = useNavigate();
  const t = useTranslations();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const isSuperAdmin =
    session?.admin?.roles?.includes(ADMIN_ROLES.SUPER_ADMIN) ?? false;
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [segment, setSegment] = useState<Segment>("all");
  const [overriddenOnly, setOverriddenOnly] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionId>("shipping");

  useEffect(() => {
    if (!isSessionLoading && !isSuperAdmin) {
      navigate(`/${locale}/admin`, { replace: true });
    }
  }, [isSessionLoading, isSuperAdmin, locale, navigate]);

  const { data: configs, isLoading, error, refetch } = useAppConfigsQuery();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (configs ?? []).filter((c) => {
      if (segment === "runtime" && c.scope !== "runtime") return false;
      if (segment === "boot" && c.scope !== "boot") return false;
      if (overriddenOnly && c.source !== "db") return false;
      if (!q) return true;
      const name = (locale === "ar" ? c.nameAr : c.nameEn) ?? "";
      return (
        c.key.toLowerCase().includes(q) || name.toLowerCase().includes(q)
      );
    });
  }, [configs, query, segment, overriddenOnly, locale]);

  // Only sections with at least one match survive, so the tab bar doubles as a
  // map of where the current search/filter actually landed.
  const sections = useMemo(() => groupConfigs(filtered), [filtered]);
  // Derived, not synced: when a filter empties the open tab we fall through to
  // the first tab that still has matches, but `activeSection` is left alone so
  // clearing the filter returns the admin to the tab they picked.
  const active =
    sections.find((s) => s.id === activeSection) ?? sections[0] ?? null;

  if (isSessionLoading || (!isSuperAdmin && !isSessionLoading) || isLoading) {
    return <LoadingSkeleton variant="list" rows={6} />;
  }
  if (error || !configs) {
    return <ErrorState onRetry={refetch} />;
  }

  const segOptions = [
    { value: "all" as const, label: t(keys.appConfigs.filterAll) },
    { value: "runtime" as const, label: t(keys.appConfigs.sectionRuntime) },
    { value: "boot" as const, label: t(keys.appConfigs.sectionBoot) },
  ];

  return (
    <div className="flex w-full flex-col gap-5 lg:px-6 lg:py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.appConfigs.title)}
          </h1>
          <p className="mt-1 text-[13.5px] text-[color:var(--text-muted)]">
            {t(keys.appConfigs.subtitle)}
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={() => setIsAddOpen(true)}
          className="h-10 rounded-lg px-4 text-sm font-semibold"
        >
          {t(keys.appConfigs.addAction)}
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full sm:max-w-xs">
          <TextInput
            name="config-search"
            type="search"
            value={query}
            placeholder={t(keys.appConfigs.searchPlaceholder)}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Segmented value={segment} onChange={setSegment} options={segOptions} />
          <label className="flex items-center gap-2 text-xs font-medium text-[color:var(--text-secondary)]">
            <ToggleInput
              name="overridden-only"
              ariaLabel={t(keys.appConfigs.overriddenOnly)}
              checked={overriddenOnly}
              onChange={(e) => setOverriddenOnly(e.target.checked)}
            />
            {t(keys.appConfigs.overriddenOnly)}
          </label>
        </div>
      </div>

      {!active ? (
        <p className="py-10 text-center text-sm text-[color:var(--text-muted)]">
          {t(keys.appConfigs.noResults)}
        </p>
      ) : (
        <>
          {/* Scrolls horizontally rather than wrapping — seven tabs with counts
              don't fit a phone width. */}
          <div className="-mx-1 overflow-x-auto px-1 pb-1">
            <Segmented
              value={active.id}
              onChange={setActiveSection}
              ariaLabel={t(keys.appConfigs.title)}
              options={sections.map((section) => ({
                value: section.id,
                label: t(SECTION_TITLE_KEY[section.id]),
                count: section.count,
              }))}
            />
          </div>

          <SectionCard title={t(SECTION_TITLE_KEY[active.id])}>
            <div className="flex flex-col gap-5">
              {active.groups.map((group) => (
                <div key={group.label ?? "_"} className="flex flex-col gap-2">
                  {group.label ? (
                    <div className="flex items-baseline gap-2">
                      <h3 className="text-[13px] font-semibold text-[color:var(--text-secondary)]">
                        {group.label}
                      </h3>
                      <span className="text-[11px] text-[color:var(--text-muted)]">
                        {group.items.length}
                      </span>
                    </div>
                  ) : null}
                  <ul className="flex flex-col gap-2">
                    {group.items.map((config) => (
                      <AppConfigRow key={config.key} config={config} />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </SectionCard>
        </>
      )}

      <AddAppConfigDialog
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
      />
    </div>
  );
}
